import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useAuth, useDomainEvent, useLazyQuery } from '@soap/modules';
import { REFETCH_C166_DOMAIN_EVENT } from '../../modules/lyw-auth-events';

/** Reuse GetUserProfile within the tab session (SOAP in-memory query cache). */
const SHELL_PROFILE_STALE_SECONDS = 60;

type ShellUserFirstNameContextValue = {
  firstName: string;
  isLoading: boolean;
};

const ShellUserFirstNameContext = createContext<ShellUserFirstNameContextValue>({
  firstName: '',
  isLoading: false,
});

export function ShellUserFirstNameProvider({ children }: { children: React.ReactNode }) {
  const { authReady, isAuthenticated } = useAuth('ShellUserFirstNameProvider');
  const [getUserProfile, queryResult] = useLazyQuery('C166v1_GetUserProfile', null);
  const initialFetchSentRef = useRef(false);

  useDomainEvent({
    eventName: REFETCH_C166_DOMAIN_EVENT,
    onEventReceived: () => {
      getUserProfile?.(undefined, 0);
    },
  });

  useEffect(() => {
    if (!authReady || !isAuthenticated || initialFetchSentRef.current) {
      return;
    }
    initialFetchSentRef.current = true;
    getUserProfile?.(undefined, SHELL_PROFILE_STALE_SECONDS);
  }, [authReady, isAuthenticated, getUserProfile]);

  const value = useMemo((): ShellUserFirstNameContextValue => {
    const firstName =
      typeof queryResult?.e138_FirstName === 'string'
        ? queryResult.e138_FirstName.trim()
        : '';
    const isLoading =
      Boolean(authReady && isAuthenticated && initialFetchSentRef.current && !queryResult);
    return { firstName, isLoading };
  }, [queryResult, authReady, isAuthenticated]);

  return (
    <ShellUserFirstNameContext.Provider value={value}>
      {children}
    </ShellUserFirstNameContext.Provider>
  );
}

export function useShellUserFirstName(): ShellUserFirstNameContextValue {
  return useContext(ShellUserFirstNameContext);
}
